import { useEffect, useId, useRef, useState } from "react";
import { Check, Folder, FolderInput, FolderPlus, Folders, Pencil, Trash2, X } from "lucide-react";
import type { StudioController } from "../useStudio";
import { cleanProjectName, projectNameExists } from "../studio/project-folders";
import "./project-folders.css";

export type ProjectFilter = string | null;
export const UNFILED_PROJECT = "__unfiled__";
type ProjectFolder = { id: string; name: string; scriptIds: string[] };

export function ProjectFolders({ studio, selected, onSelect }: {
  studio: StudioController;
  selected: ProjectFilter;
  onSelect: (id: ProjectFilter) => void;
}) {
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const newButton = useRef<HTMLButtonElement>(null);
  const nameId = useId();
  const scriptIds = new Set(studio.scripts.map(script => script.id));
  const assigned = new Set(studio.projectFolders.flatMap(folder => folder.scriptIds));
  const unfiledCount = studio.scripts.filter(script => !assigned.has(script.id)).length;
  useEffect(() => { if (editing) { input.current?.focus(); input.current?.select(); } }, [editing]);
  const edit = (folder?: ProjectFolder) => { setEditing(folder?.id || "new"); setName(folder?.name || ""); setError(""); };
  const close = () => { setEditing(null); setError(""); queueMicrotask(() => newButton.current?.focus()); };
  const save = () => {
    const cleaned = cleanProjectName(name);
    if (!cleaned) { setError("Give this project a name."); return; }
    if (projectNameExists(studio.projectFolders, cleaned, editing || undefined)) {
      setError("A project with this name already exists. Choose another name."); return;
    }
    if (editing === "new") {
      const id = studio.createProjectFolder(cleaned);
      if (!id) return;
      onSelect(id);
    } else if (editing) studio.renameProjectFolder(editing, cleaned);
    close();
  };
  return <section className="project-folders" aria-label="Project folders">
    <header className="project-folders-heading">
      <h3>Projects</h3>
      <button ref={newButton} type="button" className="icon-button" aria-label="New project" data-help="Create a project folder to keep related scripts together." onClick={() => edit()}><FolderPlus size={16} /></button>
    </header>
    {editing && <form className="project-folder-form" onSubmit={event => { event.preventDefault(); save(); }} onKeyDown={event => {
      event.stopPropagation();
      if (event.key === "Escape") { event.preventDefault(); close(); }
    }}>
      <label htmlFor={nameId}>{editing === "new" ? "Project name" : "Rename project"}</label>
      <div><input ref={input} id={nameId} aria-label="Project name" value={name} maxLength={80} autoComplete="off" onChange={event => { setName(event.target.value); setError(""); }} />
        <button type="submit" className="icon-button" aria-label={editing === "new" ? "Create project" : "Save project name"} data-help="Save this project name."><Check size={16} /></button>
        <button type="button" className="icon-button" aria-label="Cancel project edit" onClick={close}><X size={16} /></button></div>
      {error && <p role="alert">{error}</p>}
    </form>}
    <nav className="project-folders-list" aria-label="Filter scripts by project">
      <button type="button" className={`project-folder-select ${selected === null ? "active" : ""}`} aria-current={selected === null ? "page" : undefined} onClick={() => onSelect(null)} data-help="Show scripts from every project and scripts that have not been filed."><Folders size={15} /><span>All scripts</span><small>{studio.scripts.length}</small></button>
      <button type="button" className={`project-folder-select ${selected === UNFILED_PROJECT ? "active" : ""}`} aria-label="Unfiled scripts" aria-current={selected === UNFILED_PROJECT ? "page" : undefined} onClick={() => onSelect(UNFILED_PROJECT)} data-help="Show scripts that are not assigned to a project."><Folder size={15} /><span>Unfiled</span><small>{unfiledCount}</small></button>
      {studio.projectFolders.map(folder => <div className={`project-folder-row ${selected === folder.id ? "active" : ""}`} key={folder.id}>
        <button type="button" className="project-folder-select" aria-label={`Open project ${folder.name}`} aria-current={selected === folder.id ? "page" : undefined} onClick={() => onSelect(folder.id)} data-help={`Show the scripts in ${folder.name}. New and imported scripts will go into this project.`}><Folder size={15} /><span>{folder.name}</span><small>{folder.scriptIds.filter(id => scriptIds.has(id)).length}</small></button>
        <div className="project-folder-actions">
          <button type="button" className="icon-button" aria-label={`Rename project ${folder.name}`} data-help="Change this project’s name." onClick={() => edit(folder)}><Pencil size={13} /></button>
          <button type="button" className="icon-button" aria-label={`Delete project ${folder.name}`} data-help="Remove this folder. Its scripts are kept in Unfiled." onClick={() => {
            if (!window.confirm(`Remove project “${folder.name}”? All of its scripts will be kept in Unfiled.`)) return;
            studio.deleteProjectFolder(folder.id);
            if (editing === folder.id) setEditing(null);
            if (selected === folder.id) onSelect(UNFILED_PROJECT);
          }}><Trash2 size={13} /></button>
        </div>
      </div>)}
    </nav>
    {!studio.projectFolders.length && !editing && <p className="project-folder-hint">Group related scripts into projects.</p>}
  </section>;
}

export function ScriptFolderPicker({ title, folders, folderId, onMove }: {
  title: string; folders: ProjectFolder[]; folderId: string | null; onMove: (folderId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const select = useRef<HTMLSelectElement>(null);
  const id = useId();
  useEffect(() => { if (open) select.current?.focus(); }, [open]);
  const close = () => {
    setOpen(false);
    queueMicrotask(() => {
      if (trigger.current?.isConnected) trigger.current.focus();
      else document.querySelector<HTMLInputElement>('input[aria-label="Search scripts"]')?.focus();
    });
  };
  return <>
    <button ref={trigger} type="button" className="icon-button" aria-label={`Move ${title} to project`} aria-expanded={open} aria-controls={open ? id : undefined} data-help="Move this script into a project, or leave it unfiled." onClick={() => setOpen(value => !value)}><FolderInput size={13} /></button>
    {open && <div id={id} className="script-project-picker" onKeyDown={event => {
      event.stopPropagation();
      if (event.key === "Escape") { event.preventDefault(); close(); }
    }}>
      <label><span>Project</span><select ref={select} aria-label={`Project for ${title}`} value={folderId || ""} onChange={event => { onMove(event.target.value || null); close(); }}>
        <option value="">No project · Unfiled</option>
        {folders.map(folder => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
      </select></label>
      <button type="button" className="icon-button" aria-label="Close project picker" onClick={close}><X size={14} /></button>
      {!folders.length && <small>Create a project using the folder button above.</small>}
    </div>}
  </>;
}
